import type { NinjaProficiencyType } from "../proficiency-attributes.js";

export type TechniqueFamily =
  | "taijutsu"
  | "elemental_ninjutsu"
  | "fuinjutsu"
  | "medical_ninjutsu"
  | "genjutsu";

export type TechniqueProficiency = NinjaProficiencyType;

export type TechniqueTargeting =
  | "self"
  | "reactive_counter"
  | "frontal_cone"
  | "projectile"
  | "circle"
  | "point_area"
  | "ally_injured"
  | "ally_injured_or_threatened"
  | "single_hostile";

/** Reusable runtime effect families; individual jutsus provide the values. */
export type TechniqueBehaviorDefinition =
  | { kind: "damage" }
  | { kind: "healing" }
  | { kind: "control" }
  | { kind: "trap" }
  | { kind: "field" }
  | { kind: "restorative_barrier" }
  | { kind: "guard" }
  | { kind: "counter" };

export const TECHNIQUE_LEVELS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;
export type TechniqueLevel = (typeof TECHNIQUE_LEVELS)[number];

export interface TechniqueControlDefinition {
  fearMs: number;
  potency: number;
  damageReduction: number;
  debuffMs: number;
}

export type TechniqueReactiveDefinition =
  | { kind: "guard"; armsCounterReadyOnHit: true }
  | { kind: "counter"; readyWindowMs: 4_000; expiresOnNextAction: true };

/** Runtime behavior for techniques that leave a server-owned trap in the world. */
export interface TechniqueDeploymentDefinition {
  kind: "proximity_trap";
  armingTimeMs: number;
  lifetimeMs: number;
  activeLimitPerCaster: 1;
  recast: "replace";
  placement: "target_position";
}

/** Runtime behavior for server-owned area effects that pulse over time. */
export interface TechniqueFieldDefinition {
  kind: "persistent_field";
  durationMs: number;
  pulseIntervalMs: number;
  pulseCount: number;
  activeLimitPerCaster: 1;
  recast: "replace";
  placement: "target_position";
}

/** Mechanical values that may change independently at each mastery level. */
export interface TechniqueLevelParameters {
  chakraCost: number;
  cooldownMs: number;
  /** Delay between committing an action and resolving its impact. */
  impactDelayMs?: number;
  range: number;
  maxTargets: number;
  power: number;
  attackCoefficient: number;
  coneDegrees?: number;
  radius?: number;
  triggerRadius?: number;
  explosionRadius?: number;
  minimumTargets?: number;
  injuredHpThreshold?: number;
  healBase?: number;
  healCoefficient?: number;
  shieldBase?: number;
  shieldCoefficient?: number;
  shieldDurationMs?: number;
  control?: TechniqueControlDefinition;
  /** Soft-control magnitude as a fraction; e.g. 0.1 is a 10% slow. */
  fieldSlow?: number;
  damageReduction?: number;
  defenseWindowMs?: number;
  counterDamageBonus?: number;
}

export type TechniqueLevelTable = Record<
  TechniqueLevel,
  TechniqueLevelParameters
>;

/** Immutable authored mechanics for a technique, separate from ninja mastery. */
export interface TechniqueMechanicsDefinition {
  id: string;
  name: string;
  family: TechniqueFamily;
  proficiency: TechniqueProficiency;
  targeting: TechniqueTargeting;
  behavior: TechniqueBehaviorDefinition;
  /** Stable stacking identity for shields; distinct from a visual effect ID. */
  shieldKey?: string;
  reactive?: TechniqueReactiveDefinition;
  deployment?: TechniqueDeploymentDefinition;
  field?: TechniqueFieldDefinition;
  levels: TechniqueLevelTable;
}

export type ResolvedTechnique<
  T extends TechniqueMechanicsDefinition = TechniqueMechanicsDefinition,
> = Omit<T, "levels"> & TechniqueLevelParameters & { level: TechniqueLevel };
